import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Send, Plus, Trash2, Users, Loader2 } from "lucide-react";
import type { Character, RoomDetail, RoomMessage } from "../services/api";
import { getCharacters, getChatRooms, createChatRoom, getChatRoom, deleteChatRoom, sendRoomMessage } from "../services/api";

interface Props {
  onBack: () => void;
}

export function MultiChatRoom({ onBack }: Props) {
  const [rooms, setRooms] = useState<Array<{ id: string; name: string; character_ids: string[]; characters: Array<{ id: string; name: string; avatar: string }>; message_count: number; created_at: string; updated_at: string }>>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [activeRoom, setActiveRoom] = useState<RoomDetail | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [selectedChars, setSelectedChars] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeRoom?.messages]);

  async function loadData() {
    try {
      const [roomsData, charsData] = await Promise.all([
        getChatRooms(),
        getCharacters(),
      ]);
      setRooms(roomsData);
      setCharacters(charsData);
    } catch {
      // noop
    }
  }

  async function handleSelectRoom(roomId: string) {
    setLoading(true);
    setError("");
    try {
      const room = await getChatRoom(roomId);
      setActiveRoom(room);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateRoom() {
    if (!newRoomName.trim() || selectedChars.length < 2) return;
    setLoading(true);
    setError("");
    try {
      const room = await createChatRoom(newRoomName.trim(), selectedChars);
      setRooms([...rooms, room]);
      setNewRoomName("");
      setSelectedChars([]);
      setCreating(false);
      await handleSelectRoom(room.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "创建失败");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteRoom(roomId: string) {
    if (!confirm("确定删除此聊天室？")) return;
    try {
      await deleteChatRoom(roomId);
      setRooms(rooms.filter((r) => r.id !== roomId));
      if (activeRoom?.id === roomId) setActiveRoom(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "删除失败");
    }
  }

  async function handleSend() {
    if (!input.trim() || !activeRoom) return;
    const msg = input.trim();
    setInput("");
    setLoading(true);
    setError("");

    // 乐观更新：添加用户消息
    const userMsg: RoomMessage = {
      id: `msg_${Date.now()}`,
      role: "user",
      content: msg,
      charname: "你",
      timestamp: new Date().toISOString(),
    };

    setActiveRoom({
      ...activeRoom,
      messages: [...activeRoom.messages, userMsg],
    });

    try {
      const result = await sendRoomMessage({
        room_id: activeRoom.id,
        message: msg,
      });

      // 添加 AI 回复
      const aiMessages: RoomMessage[] = [];
      for (const reply of result.replies) {
        if (reply.message) {
          aiMessages.push(reply.message);
        }
      }

      setActiveRoom((prev) =>
        prev
          ? { ...prev, messages: [...prev.messages, ...aiMessages] }
          : null
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "发送失败");
      // 移除乐观更新的用户消息
      setActiveRoom((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== userMsg.id) }
          : null
      );
    } finally {
      setLoading(false);
    }
  }

  // 聊天室列表视图
  if (!activeRoom) {
    return (
      <div className="h-full overflow-y-auto bg-slate-950">
        <div className="max-w-2xl mx-auto p-6">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={onBack} className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                多人聊天室
              </h1>
              <p className="text-xs text-slate-400">与多个角色同时对话</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-400">{error}</div>
          )}

          {/* 创建聊天室 */}
          {creating ? (
            <div className="mb-6 bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-slate-200 mb-3">创建新聊天室</h3>
              <input
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="聊天室名称"
                className="w-full mb-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
              />
              <p className="text-xs text-slate-400 mb-2">选择角色 (至少 2 个):</p>
              <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto">
                {characters.map((c) => (
                  <button
                    key={c.id}
                    onClick={() =>
                      setSelectedChars(
                        selectedChars.includes(c.id)
                          ? selectedChars.filter((id) => id !== c.id)
                          : [...selectedChars, c.id]
                      )
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                      selectedChars.includes(c.id)
                        ? "bg-cyan-500 text-white"
                        : "bg-slate-800 text-slate-400 hover:text-white border border-slate-700"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateRoom}
                  disabled={!newRoomName.trim() || selectedChars.length < 2 || loading}
                  className="px-4 py-2 rounded-xl bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "创建"}
                </button>
                <button
                  onClick={() => setCreating(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-400 text-sm hover:text-white"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              className="w-full mb-6 py-3 rounded-xl bg-slate-900/50 border border-dashed border-slate-700 text-slate-400 hover:text-white hover:border-cyan-500/50 text-sm flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> 创建新聊天室
            </button>
          )}

          {/* 聊天室列表 */}
          <div className="space-y-2">
            {rooms.length === 0 && (
              <p className="text-center text-sm text-slate-500 py-8">还没有聊天室，创建一个吧</p>
            )}
            {rooms.map((room) => (
              <div
                key={room.id}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-cyan-500/30 transition-colors cursor-pointer"
                onClick={() => handleSelectRoom(room.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white">{room.name}</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRoom(room.id);
                    }}
                    className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap mb-1">
                  {room.characters.map((c) => (
                    <span key={c.id} className="px-2 py-0.5 rounded-full bg-slate-800 text-[10px] text-cyan-400">
                      {c.name}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500">{room.message_count} 条消息</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 聊天视图
  const roomChars = characters.filter((c) => activeRoom.character_ids.includes(c.id));

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* 顶部栏 */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-900/50">
        <button onClick={() => setActiveRoom(null)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h2 className="text-sm font-semibold text-white flex-1">{activeRoom.name}</h2>
        <div className="flex gap-1">
          {roomChars.map((c) => (
            <span key={c.id} className="px-2 py-0.5 rounded-full bg-slate-800 text-[10px] text-cyan-400">
              {c.name}
            </span>
          ))}
        </div>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {activeRoom.messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                msg.role === "user"
                  ? "bg-cyan-500/20 text-cyan-200"
                  : "bg-slate-800 text-slate-200"
              }`}
            >
              {msg.charname && (
                <p className="text-[10px] text-cyan-400 mb-1 font-medium">{msg.charname}</p>
              )}
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入框 */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/50">
        {error && (
          <p className="text-xs text-red-400 mb-2">{error}</p>
        )}
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder={`与 ${roomChars.map((c) => c.name).join("、")} 聊天...`}
            disabled={loading}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="px-4 py-2.5 rounded-xl bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
